/**
 * The chart vocabulary — slice 20.
 *
 * **There is no charting library in this repository and there is not going to
 * be one.** `apps/web` depends on react, react-dom, react-router-dom,
 * lucide-react, zod and the two workspace packages, and every chart on the Reach
 * dashboard is hand-authored SVG in `@wend/ui`. That is a decision, not an
 * omission: a chart library arrives with its own colour system, its own type
 * scale and its own dark-mode assumptions, and this app already has all three,
 * fixed in `CONTRACT.md` and expressed as `@theme` tokens. The cheapest version
 * of adopting one is a chart that looks like somebody else's product sitting in
 * the middle of ours; the honest version is re-theming the library, which is
 * more work than drawing a bar.
 *
 * What that costs is that every chart is written by hand, and hand-written
 * charts drift — three components each with their own idea of which colour a
 * channel is, whether a threshold line is red or amber, and what to draw when
 * there is no data. **This file is the answer to the drift, and it is the whole
 * reason a drawing vocabulary lives in `@wend/shared` at all.**
 *
 * ## THIS FILE IS NOT A PAYLOAD, AND THAT IS WHY IT IS NOT `./overview-activity.js`
 *
 * Nothing here crosses the wire. There is no zod in it and there should not be:
 * a schema would imply an API parses these shapes, and an API that started
 * sending colours would be deciding the design system from the server. The split
 * is slice 15's `onboarding.ts` / `navigation.ts` argument applied again — *only
 * one of the two is a payload* — and it means a reviewer asking "what does
 * `GET /overview/activity` return" reads one file and not two hundred lines of
 * drawing rules first.
 *
 * The arrows point **up**: this imports `./overview-activity.js` for the bucket
 * unions and `./overview.js` for the two AWS thresholds a line is drawn at, and
 * neither points back. The thresholds are imported rather than restated for the
 * reason `./list-safety.js` imports `AWS_BOUNCE_RATE_REVIEW`: the line drawn on
 * the chart and the line the KPI tile is banded against must be one number, or a
 * chart can say a day was fine while the tile beside it says the month was not.
 *
 * ## THE TWO SERIES COLOURS, AND THE ONE THEY MUST NOT BE CONFUSED WITH
 *
 * `--color-series-a` `#8B7BF8` and `--color-series-b` `#2AA8A0`. They are new
 * `@theme` tokens in `packages/ui/src/styles/theme.css` and they sit in their
 * own block, deliberately outside the status block, because the status colours
 * in this product are **semantic only** — `CONTRACT.md` says so and it is the
 * rule that keeps a green tick meaning something.
 *
 * Measured against the dark surface `#120E28`:
 *
 *   - `#8B7BF8` — contrast **5.62:1**. Comfortably past the 3:1 minimum for a
 *     graphical object and past 4.5:1 for the label beside it.
 *   - `#2AA8A0` — contrast **6.44:1**. Same.
 *
 * **The hazard the brief named is real and the measurement confirms it.**
 * `#2AA8A0` sits at hue 176° and `--color-good` `#3DD68C` at hue 151° — 25°
 * apart, both teal-green, and only 1.55:1 apart in luminance. Two marks that
 * close together are one mark to a reader glancing at a dashboard. Three things
 * are done about it, and none of them is "be careful":
 *
 *   1. **{@link CHART_SERIES_TONE_VALUES} has exactly two members and neither is
 *      named for a status.** `ChartSeriesTone` and {@link ChartThresholdTone}
 *      are disjoint string unions, so a series cannot ask for `good` and a
 *      verdict cannot ask for `series-b`. The confusion is not available to
 *      write.
 *   2. **`--color-good` is never drawn inside a plotting area.** A verdict about
 *      a chart is a badge beside it, in the flow of the page, where it is
 *      already surrounded by text. The only semantic colour permitted among the
 *      marks is a threshold line's, and those are `--color-bad` `#F2708A`
 *      (hue 348°) and `--color-warn` `#F5B851` (hue 38°) — 172° and 138° from
 *      series B respectively, which is as far from it as this palette goes.
 *   3. **Colour is never the only difference between two series.** See
 *      {@link ChartSeries.pattern}. The two series colours are nearly
 *      equiluminant — 1.15:1 against each other — so in greyscale, in forced
 *      colours, or to a reader with deuteranopia they are the same mark. The
 *      pattern is a required field with no default, so a second series drawn
 *      without one does not compile.
 *
 * ## WHAT A SERIES IS
 *
 * A named sequence of one value per bucket, **index-aligned with the bucket
 * array it came from and the same length**, where `null` means *there was
 * nothing to measure here* and `0` means *there was nothing*. Those are
 * different marks and the difference is the entire point of slice 20 — see
 * `./overview-activity.js` and {@link CHART_NO_DATA_RULE}. A series is not a
 * component's props: it carries no width, no height, no scale and no path data,
 * because two components drawing the same series differently is fine and two
 * components disagreeing about what the numbers mean is not.
 *
 * ## WHAT A THRESHOLD LINE IS
 *
 * A horizontal reference at a value in the series' **own unit**, with the
 * sentence saying what crossing it costs. It is the mechanism by which this
 * product's oldest rule reaches a chart: *a rate is never a bare percentage; it
 * is drawn against the threshold it is measured against.* On a counts chart the
 * value comes from `dailyThresholdCount` — how many of that day's messages would
 * have been 5% — so the line moves with the day's volume. And it is drawn only
 * on a day that clears `OVERVIEW_MIN_VOLUME_FOR_RATE` with a report back: a line
 * over three messages is a rate drawn, and the page prints no rate under the
 * floor. The first version drew it on every day and called that honest at three
 * messages; review disagreed, and `dailyThresholdCount` says why.
 *
 * A threshold with no label is not a threshold, it is a stray line, so `label`
 * and `sentence` are both required. {@link chartAxisMax} exists because the
 * other way to lose a threshold is to draw it off the top of the chart on the
 * healthy day when no bar comes near it.
 */
import type { Channel } from '../enums.js';
import { OVERVIEW_ACTIVITY_WINDOW_DAYS, OVERVIEW_MIN_VOLUME_FOR_RATE, OVERVIEW_RATE_THRESHOLDS } from './overview.js';
import {
  BEFORE_WORKSPACE_COPY,
  dailyThresholdCount,
  EMAIL_DAILY_MEASURE_LABELS,
  OVERVIEW_ACTIVITY_TIME_ZONE_COPY,
  WHATSAPP_DAILY_MEASURE_LABELS,
  emailDailyValues,
  whatsAppDailyValues,
  type EmailDailyBucket,
  type EmailDailyMeasure,
  type WhatsAppDailyBucket,
  type WhatsAppDailyMeasure,
} from './overview-activity.js';

/* -------------------------------------------------------------------------- */
/* The palette, by role                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The two slots a data series may use, named by **role and not by colour**.
 *
 * `series-a` and `series-b` rather than `violet` and `teal`, so that changing
 * the hex in `theme.css` does not leave every component describing the old
 * colour. Two and only two: this product's charts compare at most two things at
 * a time — two channels, or accepted against delivered — and a third slot would
 * be a request for a third validated colour that nobody has validated.
 *
 * A chart needing a third series is a chart that should be two charts.
 */
export const CHART_SERIES_TONE_VALUES = ['series-a', 'series-b'] as const;
export type ChartSeriesTone = (typeof CHART_SERIES_TONE_VALUES)[number];

/**
 * The CSS custom property each slot resolves to.
 *
 * Components read the token, never a hex. A hex in a component is a colour that
 * cannot be changed in one place and cannot be checked against `CONTRACT.md`,
 * and this codebase has one rule about that already: *do not invent colours.*
 */
export const CHART_SERIES_TOKENS: Readonly<Record<ChartSeriesTone, string>> = {
  'series-a': 'var(--color-series-a)',
  'series-b': 'var(--color-series-b)',
};

/**
 * The hexes, recorded here so the contrast reasoning in the file header can be
 * checked against a number rather than trusted.
 *
 * **This is documentation, not a source of truth.** The values that render come
 * from `@theme` in `packages/ui/src/styles/theme.css`; nothing imports this map
 * to draw with. It exists because a header that claims 6.44:1 without saying
 * which colour it measured is a claim nobody can re-run.
 */
export const CHART_SERIES_HEX_REFERENCE: Readonly<Record<ChartSeriesTone, string>> = {
  'series-a': '#8B7BF8',
  'series-b': '#2AA8A0',
};

/**
 * Which colour a channel is, everywhere on the dashboard.
 *
 * One map, so email is the same colour on the volume chart and on the outcomes
 * chart. A channel that changes colour between two charts on one screen is a
 * defect the reader diagnoses as their own misreading, which is worse than an
 * obviously wrong colour.
 */
export const CHANNEL_SERIES_TONE: Readonly<Record<Channel, ChartSeriesTone>> = {
  email: 'series-a',
  whatsapp: 'series-b',
};

/**
 * The non-colour encoding. **Required, with no default.**
 *
 * The two series colours are 1.15:1 apart in luminance — near enough to
 * identical that greyscale printing, forced-colours mode and the commonest forms
 * of colour blindness all collapse them into one mark. So every series carries a
 * second, redundant difference:
 *
 *   - `solid` / `hatched` on filled marks (bars, areas);
 *   - `solid-line` / `dashed-line` on strokes.
 *
 * It is a required field rather than an optional one with a sensible default,
 * because the failure mode of a default is that the *second* series silently
 * gets the same one as the first and nothing looks wrong on the reviewer's
 * screen.
 */
export const CHART_SERIES_PATTERN_VALUES = [
  'solid',
  'hatched',
  'solid-line',
  'dashed-line',
] as const;
export type ChartSeriesPattern = (typeof CHART_SERIES_PATTERN_VALUES)[number];

/**
 * The tones a threshold line may take. **Disjoint from {@link ChartSeriesTone}**
 * — that is what stops a series being drawn in a verdict colour and a verdict in
 * a series colour.
 *
 * `good` is not here and must not be added. A threshold is a line you do not
 * want to cross; there is no green threshold, and a green line among the marks
 * is the one that gets confused with `--color-series-b`.
 */
export const CHART_THRESHOLD_TONE_VALUES = ['warn', 'bad'] as const;
export type ChartThresholdTone = (typeof CHART_THRESHOLD_TONE_VALUES)[number];

export const CHART_THRESHOLD_TOKENS: Readonly<Record<ChartThresholdTone, string>> = {
  warn: 'var(--color-warn)',
  bad: 'var(--color-bad)',
};

/* -------------------------------------------------------------------------- */
/* A series                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * One drawable sequence.
 *
 * `values` is index-aligned with `labels` on the chart it is drawn in and is the
 * same length. A `null` is **not drawn** — no mark, and a line does not connect
 * across it. A `0` is drawn, on the baseline, because a quiet day is a fact.
 *
 * `textAlternative` is not optional and is not a title. A chart is not an image:
 * a reader on a screen reader gets nothing from "bar chart of daily sending",
 * and what they need is the numbers. {@link seriesTextAlternative} builds it
 * from the same array the marks are drawn from, so the two cannot disagree —
 * which they do the moment somebody writes the sentence by hand and then changes
 * the measure.
 */
export interface ChartSeries {
  /** Stable identity for keys and for `aria-describedby`. Not shown. */
  readonly key: string;
  /** What the legend says. A person's words: "Bounced", not "bounce_count". */
  readonly label: string;
  readonly tone: ChartSeriesTone;
  /** The redundant, non-colour difference. Required — see {@link CHART_SERIES_PATTERN_VALUES}. */
  readonly pattern: ChartSeriesPattern;
  /** What one value counts. "messages", "bounces". Used in the text alternative. */
  readonly unit: string;
  /** One entry per bucket. `null` = nothing to measure; `0` = nothing happened. */
  readonly values: readonly (number | null)[];
  /** The numbers, in words. Required — a chart is not an image. */
  readonly textAlternative: string;
}

/**
 * A horizontal reference line.
 *
 * `values` mirrors {@link ChartSeries.values} rather than being a single number,
 * because the useful threshold on a counts chart is *5% of this day's volume*
 * and that moves with the day. A constant line is expressed as an array of the
 * same value, which costs nothing and means one drawing path instead of two.
 *
 * `label` names what the line is at the point it is drawn — "Amazon reviews
 * above 5%" — and `sentence` is the fuller version for the disclosure beside the
 * chart. Both are required: an unlabelled line is a stray line, and this
 * product's oldest copy rule is that a rate means nothing until the reader knows
 * which side of it is bad.
 */
export interface ChartThreshold {
  readonly key: string;
  /** Printed at the line. Names the value and which side is bad. */
  readonly label: string;
  /** The fuller sentence, for the disclosure beside the chart. */
  readonly sentence: string;
  readonly tone: ChartThresholdTone;
  /**
   * One entry per bucket, `null` where no line is drawn — nothing to be a
   * fraction of, no report back, or a day under the rate floor.
   */
  readonly values: readonly (number | null)[];
  /**
   * The line in words: its value on each day it is drawn. Required for the
   * reason a series' is. `TimeSeriesChart` promises that every number it draws
   * is also text, and the line's numbers were the ones missing: review found a
   * screen reader told "3" for a day's bounces and then a standing sentence
   * about "5% of each day's accepted messages", with that day's accepted count
   * on a different chart — so "did the bar cross the line" was not answerable.
   */
  readonly textAlternative: string;
}

/**
 * What a chart must say instead of drawing, and the rule behind it.
 *
 * {@link CHART_NO_DATA_RULE} is written down because it is the one thing slice
 * 20 was deferred five slices to get right, and because it is invisible in a
 * screenshot: a flat line at zero looks like a chart working.
 */
export const CHART_NO_DATA_RULE =
  'A chart with nothing to draw says so. It never draws a flat line along the baseline, because a flat line at zero is what a quiet week looks like, and telling those apart is the reader’s whole reason for looking.';

/**
 * The words in the empty state where a chart would have been. Four, then the
 * reason.
 *
 * **For a series with nothing measured in it, and nothing else.** A measured
 * series whose every count is a real zero is not this state and must not borrow
 * this sentence — see {@link WHATSAPP_NONE_UNDELIVERABLE_COPY} and
 * {@link EMAIL_NONE_REPORTED_COPY}, the two places that distinction is reachable.
 */
export const CHART_EMPTY_COPY = {
  short: 'Nothing to chart yet.',
  detail: BEFORE_WORKSPACE_COPY.detail,
} as const;

/** The axis title every daily chart prints. One source, so no chart omits the zone. */
export const CHART_DAILY_AXIS_LABEL = OVERVIEW_ACTIVITY_TIME_ZONE_COPY.axisLabel;

/* -------------------------------------------------------------------------- */
/* Building one                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The email series for one measure, with its text alternative already attached.
 *
 * This is the only supported way to make one. Building a `ChartSeries` literal
 * by hand is possible and is how the label, the values and the text alternative
 * come apart — the text alternative is the half nobody looks at, so it is the
 * half that goes stale, and a screen reader then hears last week's measure.
 */
export function emailChartSeries(input: {
  readonly buckets: readonly EmailDailyBucket[];
  readonly measure: EmailDailyMeasure;
  readonly tone: ChartSeriesTone;
  readonly pattern: ChartSeriesPattern;
  readonly unit?: string;
}): ChartSeries {
  const values = emailDailyValues(input.buckets, input.measure);
  const label = EMAIL_DAILY_MEASURE_LABELS[input.measure];
  const unit = input.unit ?? 'messages';
  return {
    key: `email-${input.measure}`,
    label,
    tone: input.tone,
    pattern: input.pattern,
    unit,
    values,
    textAlternative: seriesTextAlternative({
      label,
      unit,
      dates: input.buckets.map((bucket) => bucket.date),
      values,
    }),
  };
}

/** The WhatsApp half. Same rule. See {@link emailChartSeries}. */
export function whatsAppChartSeries(input: {
  readonly buckets: readonly WhatsAppDailyBucket[];
  readonly measure: WhatsAppDailyMeasure;
  readonly tone: ChartSeriesTone;
  readonly pattern: ChartSeriesPattern;
  readonly unit?: string;
}): ChartSeries {
  const values = whatsAppDailyValues(input.buckets, input.measure);
  const label = WHATSAPP_DAILY_MEASURE_LABELS[input.measure];
  const unit = input.unit ?? 'messages';
  return {
    key: `whatsapp-${input.measure}`,
    label,
    tone: input.tone,
    pattern: input.pattern,
    unit,
    values,
    textAlternative: seriesTextAlternative({
      label,
      unit,
      dates: input.buckets.map((bucket) => bucket.date),
      values,
    }),
  };
}

/**
 * The chart, in words, carrying **every number the marks carry**.
 *
 * Not a summary. "Sending rose over the period" is a claim this function cannot
 * check and a reader cannot verify; the numbers are the alternative, and a
 * reader who cannot see the bars is entitled to the same data the bars are made
 * of, not a description of their shape.
 *
 * A `null` is rendered as {@link NO_MEASUREMENT_PHRASE} and a `0` as `0`, so the
 * distinction the whole slice turns on survives into the text. Rendering `null`
 * as "0" here would put the lie back in the one place nobody reviews.
 *
 * The zone is named once at the front, because every date in the list is a UTC
 * day and repeating it thirty-one times is noise.
 */
export function seriesTextAlternative(input: {
  readonly label: string;
  readonly unit: string;
  readonly dates: readonly string[];
  readonly values: readonly (number | null)[];
}): string {
  const entries = input.dates.map((date, index) => {
    const value = input.values[index];
    return `${date}: ${value === null || value === undefined ? NO_MEASUREMENT_PHRASE : String(value)}`;
  });
  return `${input.label}, ${input.unit} per day (UTC). ${entries.join('; ')}.`;
}

/** How a `null` reads in a text alternative. Never "0", never "—". */
export const NO_MEASUREMENT_PHRASE = 'not measured';

/**
 * The words around the table of figures every daily chart can open.
 *
 * SVG text scales with the chart it is drawn in: the axis type is about four
 * pixels tall on a phone and about eight in the Overview's narrower column on a
 * laptop, and the daily numbers were nowhere else a sighted reader could reach —
 * the text alternative is `sr-only`. Review found it. So every chart carries its
 * own figures as a table at the page's type size, one click away. A `null` in a
 * series reads as it does in the text alternative, and a day with no threshold
 * line says so in words rather than with a dash — the rule
 * {@link NO_MEASUREMENT_PHRASE} keeps.
 */
export const CHART_FIGURES_COPY = {
  summary: 'The daily figures, as a table',
  notMeasured: NO_MEASUREMENT_PHRASE,
  noLine: 'no line',
} as const;

/**
 * A threshold line in words: its value on each day it is drawn, and the other
 * days named once.
 *
 * Not {@link seriesTextAlternative}. A day with no line is not "not measured" —
 * its bounces may well have been — it is a day the line does not apply to, and
 * saying so thirty times would bury the few days that carry one.
 *
 * Read to two decimal places at most: the complaint line on a day of 1,200 is
 * 1.2 messages, and a screen reader handed 1.2000000000000002 has been handed
 * floating-point arithmetic rather than a number. That rounding is for the
 * words only; the line itself is drawn at the exact value.
 */
export function thresholdTextAlternative(input: {
  readonly label: string;
  readonly dates: readonly string[];
  readonly values: readonly (number | null)[];
}): string {
  const drawn = input.dates.flatMap((date, index) => {
    const value = input.values[index];
    return value === null || value === undefined || !Number.isFinite(value)
      ? []
      : [`${date}: ${String(Math.round(value * 100) / 100)}`];
  });
  if (drawn.length === 0) return `${input.label}: no line is drawn on any day.`;
  const others = drawn.length === input.dates.length ? '' : ' No line on the other days.';
  return `${input.label}, in messages, on each day it is drawn (UTC): ${drawn.join('; ')}.${others}`;
}

/* -------------------------------------------------------------------------- */
/* The threshold lines, and the chart that deliberately has none               */
/* -------------------------------------------------------------------------- */

/**
 * The two AWS review lines, as they read on a chart.
 *
 * Both percentages are interpolated from `OVERVIEW_RATE_THRESHOLDS` rather than
 * typed into the sentence: AWS publishes these numbers and can move them, and a
 * label naming 5% over a line drawn at something else is worse than no label.
 *
 * `tone: 'bad'` on both. AWS's line is the point at which an account goes under
 * review, which is not a warning — `OVERVIEW_WARNING_FRACTION_OF_THRESHOLD`
 * exists and a second amber line at half the threshold is expressible with
 * `tone: 'warn'`, but it is deliberately not drawn by default: two horizontal
 * lines across thirty bars is a chart nobody reads, and the amber verdict
 * already reaches the reader as the KPI tile's band.
 */
export const EMAIL_CHART_THRESHOLD_VALUES = ['bounce', 'complaint'] as const;
export type EmailChartThreshold = (typeof EMAIL_CHART_THRESHOLD_VALUES)[number];

export const EMAIL_CHART_THRESHOLD_COPY: Readonly<
  Record<EmailChartThreshold, { readonly label: string; readonly sentence: string }>
> = {
  bounce: {
    label: `Amazon reviews above ${(OVERVIEW_RATE_THRESHOLDS.bounce * 100).toFixed(0)}%`,
    sentence: `The line is ${(OVERVIEW_RATE_THRESHOLDS.bounce * 100).toFixed(0)}% of a day’s messages with a delivery or a bounce reported, drawn on each day with at least ${OVERVIEW_MIN_VOLUME_FOR_RATE.toLocaleString('en-GB')} of them. Amazon reviews a sending account whose bounce rate climbs past it, and under bring-your-own-SES that account is yours.`,
  },
  complaint: {
    label: `Amazon reviews above ${(OVERVIEW_RATE_THRESHOLDS.complaint * 100).toFixed(1)}%`,
    sentence: `The line is ${(OVERVIEW_RATE_THRESHOLDS.complaint * 100).toFixed(1)}% of a day’s messages with a delivery or a bounce reported — one in a thousand — drawn on each day with at least ${OVERVIEW_MIN_VOLUME_FOR_RATE.toLocaleString('en-GB')} of them. Amazon reviews a sending account whose spam-complaint rate climbs past it, and under bring-your-own-SES that account is yours.`,
  },
};

/**
 * What the email outcome charts say when no day in their window carries a
 * review line — every day under the rate floor, or unreported. Without it the
 * reader looks for the dashed line the charts are described by and finds none.
 *
 * Printed **once, under both** email charts, not once under each. The bounce and
 * complaint lines come from `dailyThresholdCount` over the same days with a
 * positive threshold each, so they are drawn on exactly the same days — both or
 * neither — and the same sentence stacked under two charts is how a reader
 * learns the text under a chart is boilerplate. "Neither chart has one" is true
 * whether a chart drew bars or said nothing bounced.
 */
export const EMAIL_CHART_THRESHOLD_FLOOR_COPY = `Amazon's review line is drawn only on a day with at least ${OVERVIEW_MIN_VOLUME_FOR_RATE.toLocaleString('en-GB')} emails whose delivery or bounce has been reported — below that, one day's rate is noise — and not on a day whose reports are bounces alone while its other mail still waits. No day in this period qualified, so neither chart has one.`;

/**
 * The AWS line, drawn across a **counts** chart at what that day's volume makes
 * it.
 *
 * This is the whole mechanism by which *a rate is never a bare percentage*
 * reaches a chart without a per-day rate ever being computed. `dailyThresholdCount`
 * does the arithmetic, this attaches the words, and the AWS figure comes from
 * `OVERVIEW_RATE_THRESHOLDS` — the same constant the KPI tile is banded against
 * — so the line on the chart and the verdict on the tile cannot come from two
 * different numbers.
 *
 * The line is `null` on every bucket `dailyThresholdCount` refuses — a quiet
 * day, a day nobody has reported on, and a day under the rate floor — so it
 * simply stops there rather than running along the baseline and implying that
 * zero messages is zero tolerance, or drawing a verdict over three messages.
 * {@link EMAIL_CHART_THRESHOLD_FLOOR_COPY} is what a chart prints when that
 * leaves no line at all.
 */
export function emailThresholdLine(
  buckets: readonly EmailDailyBucket[],
  which: EmailChartThreshold,
): ChartThreshold {
  const copy = EMAIL_CHART_THRESHOLD_COPY[which];
  const threshold = OVERVIEW_RATE_THRESHOLDS[which];
  const values = buckets.map((bucket) => dailyThresholdCount(bucket, threshold));
  return {
    key: `email-${which}-threshold`,
    label: copy.label,
    sentence: copy.sentence,
    tone: 'bad',
    values,
    textAlternative: thresholdTextAlternative({
      label: copy.label,
      dates: buckets.map((bucket) => bucket.date),
      values,
    }),
  };
}

/**
 * **The WhatsApp chart has no threshold line, and that is a decision.**
 *
 * There is no `whatsAppThresholdLine`, and one must not be added out of
 * symmetry. Two candidates exist and both are wrong here:
 *
 *   - **Meta publishes no rate.** A number's quality rating falls when messages
 *     are not delivered, and Meta says neither at what rate nor over what
 *     window. Drawing an invented line would give a made-up figure the same
 *     visual weight as AWS's two real ones — the reason
 *     `overviewSendingHealthSchema` gives for `deliverability` having no
 *     threshold either.
 *   - **`WHATSAPP_SEND_RISK_GATE.haltRate` is a real line this product
 *     enforces, over a different denominator.** Slice 18 measures it per
 *     campaign over *settled* outcomes, and settled is not accepted — that is
 *     the decision slice 18 turns on, and `accepted` was removed from
 *     `SendRiskMeasurement` entirely so it could never become the denominator.
 *     Drawing that rate against a day's accepted count would put it back.
 *
 * So the WhatsApp chart shows counts and says what it does not know.
 */
export const WHATSAPP_NO_THRESHOLD_COPY = {
  short: 'No published limit to draw.',
  detail:
    'Amazon publishes the rates at which it reviews an email account, so those lines can be drawn. Meta publishes no equivalent for WhatsApp — a number’s quality rating falls when messages are not delivered, but not at a rate anybody outside Meta can name. Wend stops a campaign of its own accord when too many of its messages cannot be delivered; that check is measured per campaign, on its own screen.',
} as const;

/**
 * **The empty state's mirror image, and the one this file originally got
 * wrong.**
 *
 * {@link chartAxisMax} returns `null` for a series that is entirely zeros with
 * no threshold over it, so a chart of *undeliverable WhatsApp messages* for a
 * workspace that sent every day and had nothing fail has no scale — and the
 * caller then reached for {@link CHART_EMPTY_COPY}, which says "Nothing to chart
 * yet". That is the sentence for a channel nobody connected. Printed over three
 * weeks of clean sending it tells a reader with a perfect month that Wend does
 * not know, which is this slice's own rule — *zero and not measured are
 * different* — broken in the direction nobody was watching, because the
 * inversion looks like the safe answer.
 *
 * So a measured channel with every count at zero says **what is true**: nothing
 * failed. It is a fact about the period, not an absence of one, and it is the
 * best news this card can carry.
 *
 * **And it claims only what was reported.** The first wording was "No WhatsApp
 * message failed to be delivered in the last thirty days", which is a statement
 * about every message — including today's, which Meta may not have answered for
 * yet, and on a deployment whose status webhook is not reaching Wend, all of
 * them. A day with no report on it is now `null` rather than `0` (see
 * `whatsAppDailyValues`), so this branch needs at least one reported day to be
 * reachable, and the sentence is scoped to the messages Meta has reported on.
 * A channel with none reported gets `NO_OUTCOME_REPORTED_COPY` instead.
 *
 * This comment used to say there was no email counterpart and that one must not
 * be added out of symmetry, because the two email charts always carried
 * {@link emailThresholdLine}, above zero on every day a measured channel had, so
 * they always scaled. That stopped being true when {@link dailyThresholdCount}
 * stopped drawing the line on a day under the rate floor: a clean month of a few
 * hundred emails a day has no line and no bar to scale by, and fell straight
 * into "Nothing to chart yet". {@link EMAIL_NONE_REPORTED_COPY} exists for that
 * reason, not for symmetry.
 */
export const WHATSAPP_NONE_UNDELIVERABLE_COPY = `Of the WhatsApp messages Meta has reported on in the last ${OVERVIEW_ACTIVITY_WINDOW_DAYS} days, none failed to be delivered.`;

/**
 * What an email outcome chart says over a reported window whose every count is
 * zero and on which no day carried Amazon's line — so there is nothing to scale
 * by. The email form of {@link WHATSAPP_NONE_UNDELIVERABLE_COPY}, needed for the
 * reason its comment gives, and scoped the same way: to what Amazon reported
 * on. A day with no report is `null`, not `0`, and the chart is not mounted at
 * all unless some day in its window has one.
 *
 * "Has been reported as spam" rather than "was": a complaint arrives when a
 * recipient presses the button, which can be days after the delivery report, so
 * this is a statement about now.
 */
export const EMAIL_NONE_REPORTED_COPY = {
  bounce: `Of the email Amazon has reported on in the last ${OVERVIEW_ACTIVITY_WINDOW_DAYS} days, none bounced.`,
  complaint: `Of the email Amazon has reported on in the last ${OVERVIEW_ACTIVITY_WINDOW_DAYS} days, none has been reported as spam.`,
} as const satisfies Record<'bounce' | 'complaint', string>;

/**
 * The top of the value axis — **including the threshold**.
 *
 * The bug this exists to prevent is specific and quiet: a workspace with a
 * healthy day has no bar anywhere near the 5% line, so a scale fitted to the
 * bars alone puts the threshold above the top of the chart. It is not drawn, no
 * warning is produced, and the reader sees an unannotated bar chart on the one
 * screen whose entire purpose is drawing rates against the line they are
 * measured against.
 *
 * Returns `null` when there is nothing to scale — every value `null`, or every
 * value `0` with no threshold. A caller getting `null` must draw the empty
 * state, not a chart with an invented axis: {@link CHART_NO_DATA_RULE}.
 *
 * A day of all zeros with a threshold present still scales, off the threshold
 * alone, because the line is a real thing to draw and the flat row of zero bars
 * beneath it is true — **provided they are zeros.** That proviso is not
 * checkable here: this function sees numbers, not whether a provider reported
 * them. `emailDailyValues` answers `null` rather than `0` for an outcome on an
 * activity day with no report, but a quiet day is still a true `0` — so an
 * unreported month arrives as nulls *and* zeros, and `hasDrawableValues` counts
 * the zeros. (This comment used to say that gate refused such a month; it does
 * not, and review checked.) What refuses it is one level up: the outcome charts
 * mount nothing unless some day in their window has a report, and the page asks
 * `noOutcomeReported` before that and prints why.
 */
export function chartAxisMax(
  series: readonly ChartSeries[],
  thresholds: readonly ChartThreshold[] = [],
): number | null {
  const drawable: number[] = [];
  for (const one of series) {
    for (const value of one.values) {
      if (value !== null && Number.isFinite(value)) drawable.push(value);
    }
  }
  for (const line of thresholds) {
    for (const value of line.values) {
      if (value !== null && Number.isFinite(value)) drawable.push(value);
    }
  }
  if (drawable.length === 0) return null;
  const max = drawable.reduce((highest, value) => Math.max(highest, value), 0);
  // Every value was zero and no threshold reached above it: there is a chart to
  // draw (a row of true zeros) but no scale, so the caller decides. Returning 0
  // rather than null would invite a division by it.
  return max === 0 ? null : max;
}
